import React from 'react';
import { Navigate } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';

import { useCurrentChatPlan, useCurrentAvaPlan, useCurrentPlan } from '@/api';
import { useDocumentTitle } from '@/hooks';
import {
  plansPath, settingsCurrentPlanForbiddenPath, settingsManageCurrentPlanPath
} from '@/routes';
import { isNotFoundError } from '@/errors';
import { useAuth } from '@/lib/auth';

import Button from '@/components/Button';
import CurrentAccountBanner from '@/components/CurrentAccountBanner';
import CurrentPlanCard from '@/components/Billing/CurrentPlanCard';
import Icon from '@/components/Icon';
import Link from '@/components/Link';
import Main from '@/components/Main';

import PaymentDetailsCard from '@/components/PaymentDetailsCard';
import PaymentWarningCard from '@/components/PaymentWarningCard';
import WhatsappAddonsCard from '@/components/WhatsappAddonsCard';
import AgendorChatAddonsCard from '@/components/AgendorChatAddonsCard';
import AvaAddonsCard from '@/components/AvaAddonsCard';
import MobileAppsAddonsCard from '@/components/MobileAppsAddonsCard';

const PAGE_TITLE = 'Plano';
const PAGE_SUBTITLE = `Gerencie seu plano no Agendor,
método de pagamento e outras informações importantes.`;

function CurrentPlan() {
  useDocumentTitle(PAGE_TITLE);

  const { data: { data } = {}, isSuccess } = useCurrentPlan();
  const {
    data: { data: chatData } = {},
    isSuccess: isChatSuccess,
    error: chatError
  } = useCurrentChatPlan();

  const {
    data: { data: avaData } = {},
    isSuccess: isAvaSuccess,
    error: avaError
  } = useCurrentAvaPlan();

  const { user } = useAuth();
  const canSeePlansTab = user.privileges.account_admin;

  if (!canSeePlansTab) {
    return <Navigate to={settingsCurrentPlanForbiddenPath()} />;
  }

  const isTrial = data?.subscriptionType === 'trial';
  const isChatTrial = chatData?.subscriptionType === 'trial';
  const isAvaTrial = avaData?.subscriptionType === 'trial';
  const managePlanUrl = settingsManageCurrentPlanPath();

  const chatNotFound = chatError && isNotFoundError(chatError);
  const avaNotFound = avaError && isNotFoundError(avaError);

  if (!isSuccess || (!isChatSuccess && !chatNotFound) || (!isAvaSuccess && !avaNotFound)) {
    return <div className='flex-grow-1 bg-light' />;
  }

  if (isTrial) {
    return <Navigate to={plansPath()} />;
  }

  const showChatSection = !chatNotFound && !isChatTrial;
  const showAvaSection = !avaNotFound && !isAvaTrial;

  return (
    <Main fluid className='py-8 ps-4 pe-8'>
      <Container>
        {data?.paymentWarning &&
          <PaymentWarningCard
            type={data.paymentWarning}
            blockAccessDate={data.blockAccessDate}
            expirationDate={data.expirationDate}
          />
        }
        <h2 className='mb-2'>{PAGE_TITLE}</h2>

        <div className='d-flex align-items-center'>
          <p className='text-dark-gray'>{PAGE_SUBTITLE}</p>

          <Button
            as={Link}
            href={managePlanUrl}
            size='md'
            className='ms-auto mb-3 align-self-start fw-bold text-nowrap'
          >
            <Icon name='config' className='me-1' />
            Gerenciar plano
          </Button>
        </div>

        <CurrentAccountBanner
          subscriptionType={data.subscriptionType}
          expirationDate={data.expirationDate}
          isSuccess={isSuccess}
          canceled={data.canceled}
          name={data.name}
          trialAvailable={data.trialAvailable}
        />

        <Row className='mb-2'>
          <Col sm={7} className='pe-1'>
            <CurrentPlanCard type='crm' data={data} />
          </Col>
          <Col sm={5} className='ps-1'>
            <PaymentDetailsCard
              expired={data.expired}
              blocked={data.blocked}
              canceled={data.canceled}
              subscriptionType={data.subscriptionType}
              periodicity={data.periodicity}
              expirationDate={data.expirationDate}
              method={data.paymentInfo?.method}
              lastFourDigits={data.paymentInfo?.lastFourDigits}
            />
          </Col>
        </Row>

        {showChatSection && (
          <Row className='mb-2'>
            <Col sm={7} className='pe-1'>
              <CurrentPlanCard type='chat' data={chatData} />
            </Col>
            <Col sm={5} className='ps-1'>
              <PaymentDetailsCard
                expired={chatData.expired}
                blocked={chatData.blocked}
                canceled={chatData.canceled}
                subscriptionType={chatData.subscriptionType}
                periodicity={chatData.periodicity}
                expirationDate={chatData.expirationDate}
                method={chatData.paymentInfo?.method}
                lastFourDigits={chatData.paymentInfo?.lastFourDigits}
              />
            </Col>
          </Row>
        )}

        {showAvaSection && (
          <Row className='mb-2'>
            <Col sm={7} className='pe-1'>
              <CurrentPlanCard type='ava' data={avaData} />
            </Col>
            <Col sm={5} className='ps-1'>
              <PaymentDetailsCard
                expired={avaData.expired}
                blocked={avaData.blocked}
                canceled={avaData.canceled}
                subscriptionType={avaData.subscriptionType}
                periodicity={avaData.periodicity}
                expirationDate={avaData.expirationDate}
                method={avaData.paymentInfo?.method}
                lastFourDigits={avaData.paymentInfo?.lastFourDigits}
              />
            </Col>
          </Row>
        )}
        <div className='p-4 border rounded border-light text-dark-gray mb-2'>
          <h5>Complementos</h5>
          {
            showAvaSection && (
              <>
                {/*
                  Atualmente isPaid sempre será true.
                  Quando esse complemento voltar a ser exibidor para todos,
                  ele fará sentido aqui para Ava.
                */}
                <AvaAddonsCard isPaid={showAvaSection} />

                <hr />
              </>
            )
          }
          <AgendorChatAddonsCard isPaid={showChatSection} />
          <hr />
          <WhatsappAddonsCard />
          <hr />
          <MobileAppsAddonsCard />
        </div>
      </Container>
    </Main>
  );
}

export default CurrentPlan;
